import {
  Component,
  inject,
  signal,
  ChangeDetectionStrategy,
  OnInit,
  AfterViewInit,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import {
  LoginDTO,
  FormErrors,
  CompanyContextDTO,
} from '../../../types/auth.types';
import { AuthStore } from '../../../store/auth.store';
import { ValidationUtils } from '../../../utils/validation.utils';
import {
  ErrorAlertComponent,
  TextInputComponent,
  LoadingSkeletonComponent,
  ServicePermissionUtils,
  SERVICE_KEY,
} from '@sandbox-platform/shared-ui';
import {
  GoogleLoginButtonComponent,
  HeaderComponent,
  SubmitLoginButtonComponent,
} from './components';
import { AuthBannerComponent } from '../components';

@Component({
  standalone: true,
  imports: [
    FormsModule,
    RouterModule,
    ErrorAlertComponent,
    TextInputComponent,
    LoadingSkeletonComponent,
    HeaderComponent,
    SubmitLoginButtonComponent,
    GoogleLoginButtonComponent,
    AuthBannerComponent,
  ],
  selector: 'app-login',
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'animate-fadeIn',
  },
})
export class LoginComponent implements OnInit, AfterViewInit {
  readonly authStore = inject(AuthStore);
  private router = inject(Router);

  // Component loading state
  isComponentReady = signal<boolean>(false);

  // Company selection state
  companyOptions = signal<CompanyContextDTO[]>([]);

  // Form data
  formData = signal<LoginDTO>({
    identifier: '',
    password: '',
  });

  // Form validation errors
  formErrors = signal<FormErrors>({});

  constructor() {
    // Clear any existing errors when component loads
    this.authStore.clearError();
  }

  ngOnInit(): void {
    // Simulate component initialization and smooth loading
    setTimeout(() => {
      this.isComponentReady.set(true);
      // Focus identifier input after component is ready and DOM is updated
      this.focusIdentifierInput();
    }, 50);
  }

  ngAfterViewInit(): void {
    // Additional fallback to focus identifier input if needed
    if (this.isComponentReady()) {
      this.focusIdentifierInput();
    }
  }

  private focusIdentifierInput(): void {
    // Use setTimeout to ensure DOM is fully rendered
    setTimeout(() => {
      // Find the identifier input element by ID since ViewChild approach is complex with custom components
      const identifierInput = document.getElementById(
        'identifier'
      ) as HTMLInputElement;
      if (identifierInput) {
        identifierInput.focus();
      }
    }, 1000);
  }

  validateField(fieldName: keyof LoginDTO): void {
    const data = this.formData();
    const errors = { ...this.formErrors() };

    switch (fieldName) {
      case 'identifier': {
        const identifierError = ValidationUtils.validateIdentifier(
          data.identifier
        );
        if (identifierError) {
          errors[fieldName] = identifierError;
        } else {
          delete errors[fieldName];
        }
        break;
      }
      case 'password': {
        const passwordError = ValidationUtils.validateRequired(
          data.password,
          'Password'
        );
        if (passwordError) {
          errors[fieldName] = passwordError;
        } else {
          delete errors[fieldName];
        }
        break;
      }
    }

    this.formErrors.set(errors);
  }

  validateForm(): FormErrors {
    const data = this.formData();
    return ValidationUtils.validateLoginForm(data.identifier, data.password);
  }

  isFormValid(): boolean {
    const data = this.formData();
    const errors = this.validateForm();
    return (
      !ValidationUtils.hasErrors(errors) &&
      data.identifier.length > 0 &&
      data.password.length > 0
    );
  }

  async onSubmit(): Promise<void> {
    if (this.companyOptions().length > 0) {
      return;
    }

    if (!this.isFormValid()) {
      this.formErrors.set(this.validateForm());
      return;
    }

    try {
      const result = await this.authStore.login(this.formData());

      if (result.status === 'company-selection-required') {
        this.companyOptions.set(result.companies);
        return;
      }

      if (this.authStore.isLoggedIn()) {
        if (this.authStore.requiresPasswordReset()) {
          this.router.navigate(['/force-password-reset']);
        } else {
          // Check user's accessible services to determine routing
          const user = this.authStore.user();
          const accessibleServices =
            ServicePermissionUtils.getAccessibleServices(user);

          // If user ONLY has expert service access, route directly to expert module
          if (
            accessibleServices.length === 1 &&
            accessibleServices[0] === SERVICE_KEY.EXPERT
          ) {
            window.location.href = '/expert/dashboard';
          } else {
            // User has multiple services or other services - show switchboard
            this.router.navigate(['/switchboard']);
          }
        }
      }
    } catch (error) {
      // Error handling is done in the store
      console.error('Login error:', error);
    }
  }

  async onCompanySelected(company: CompanyContextDTO): Promise<void> {
    const payload: LoginDTO = {
      ...this.formData(),
      company_id: company.id,
    };

    try {
      const result = await this.authStore.login(payload);

      if (result.status === 'company-selection-required') {
        // In case backend still requires selection (e.g. stale list), update options.
        this.companyOptions.set(result.companies);
        return;
      }

      this.companyOptions.set([]);

      if (this.authStore.isLoggedIn()) {
        if (this.authStore.requiresPasswordReset()) {
          this.router.navigate(['/force-password-reset']);
        } else {
          // Check user's accessible services to determine routing
          const user = this.authStore.user();
          const accessibleServices =
            ServicePermissionUtils.getAccessibleServices(user);

          // If user ONLY has expert service access, route directly to expert module
          if (
            accessibleServices.length === 1 &&
            accessibleServices[0] === SERVICE_KEY.EXPERT
          ) {
            window.location.href = '/expert/dashboard';
          } else {
            // User has multiple services or other services - show switchboard
            this.router.navigate(['/switchboard']);
          }
        }
      }
    } catch (error) {
      console.error('Company selection error:', error);
    }
  }

  clearCompanySelection(): void {
    this.companyOptions.set([]);
    this.authStore.clearError();
  }

  onGoogleSignIn(): void {
    this.authStore.clearError();
  }
}
